import { useRef, useEffect, useState, useCallback } from "react";
import { getLanguageFromCode } from "../library";
import i18n from "../language/i18n";
import {
  convertActivityTemplateChatSpeechToTextWithAI,
  convertSubjectDescriptionChatSpeechToTextWithAI,
} from "../requests/AIRequests";
import {
  createOrUpdateStorageFileFromActivityTemplateChatAndActivityTemplateChatItem,
  createOrUpdateStorageFileFromSubjectDescriptionAndSubjectDescriptionChatAndSubjectDescriptionChatItem,
} from "../requests/StorageRequests";
import { NotificationManager } from "../notifications/NotificationManager";
import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

export const useAudioRecording = ({
  audioState,
  dispatchAudio,
  chatMode,
  selectedMicrophoneId,
  recordingAudioContextRef,
  recordingAnalyserRef,
  recordingDataArrayRef,
  recordingCanProceed,
  setRecordingCanProceed,
  onSendMessage,
  onError,
  apiParams,
}) => {
  const {
    userInformation,
    userProfile,
    curriculumType,
    brandId,
    campusId,
    institutionId,
    chatActivityId,
    chatActivityTemplateId,
    chatActivityTemplateType,
    chatSubjectDescriptionId,
    chatVoiceType,
    chatVoiceLanguage,
    chatActType,
  } = apiParams;

  const recordingSourceRef = useRef(null);
  const recordingMediaRef = useRef(null);
  const recordingAudioChunksRef = useRef([]);
  const recordingStreamRef = useRef(null);
  const recordingCheckSilenceTimeoutRef = useRef(null);
  const recordingCheckSilenceMaxTimeoutRef = useRef(null);
  const recordingCheckSilenceUserSpokenRef = useRef(false);
  const recordingIsStoppedRef = useRef(false);
  const recordingIsActiveRef = useRef(false);
  const shouldProcessAudioRef = useRef(false);

  const [recordingIsActive, setRecordingIsActive] = useState(false);
  const [streamReady, setStreamReady] = useState(false);

  // Stabilize onError so it doesn't retrigger the stream effect on every render
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const SILENCE_THRESHOLD = 0.01;
  const SOUND_THRESHOLD = 0.025;
  const SILENCE_DURATION = 2000;
  const MAX_RECORD_DURATION = 20000;

  const stopRecording = useCallback(() => {
    if (recordingIsStoppedRef.current) {
      return;
    }

    recordingIsStoppedRef.current = true;

    if (recordingCheckSilenceTimeoutRef.current) {
      clearTimeout(recordingCheckSilenceTimeoutRef.current);
      recordingCheckSilenceTimeoutRef.current = null;
    }

    if (recordingCheckSilenceMaxTimeoutRef.current) {
      clearTimeout(recordingCheckSilenceMaxTimeoutRef.current);
      recordingCheckSilenceMaxTimeoutRef.current = null;
    }

    if (recordingCheckSilenceUserSpokenRef.current) {
      // Ref doubles as timeout id (number) and "user spoken" flag (boolean) — only clear when numeric.
      if (typeof recordingCheckSilenceUserSpokenRef.current === "number") {
        clearTimeout(recordingCheckSilenceUserSpokenRef.current);
      }
      recordingCheckSilenceUserSpokenRef.current = false;
    }

    try {
      if (recordingMediaRef.current) {
        if (
          recordingMediaRef.current.state === "recording" ||
          recordingMediaRef.current.state === "paused"
        ) {
          if (typeof recordingMediaRef.current.requestData === "function") {
            try {
              recordingMediaRef.current.requestData();
            } catch (err) {
              console.warn(
                "Could not request final data from MediaRecorder:",
                err,
              );
            }
          }
          recordingMediaRef.current.stop();
        }
        recordingMediaRef.current = null;
      }
    } catch (err) {
      console.error("[recording] Error handling MediaRecorder:", err);
    }

    setRecordingIsActive(false);
    recordingIsStoppedRef.current = false;
    setRecordingCanProceed(true);
  }, [setRecordingCanProceed]);

  const discardRecording = useCallback(() => {
    shouldProcessAudioRef.current = false;
    stopRecording();
  }, [stopRecording]);

  useEffect(() => {
    recordingIsActiveRef.current = recordingIsActive;
  }, [recordingIsActive]);

  // Persistent mic stream: acquire once per AUDIO session so press-to-talk is instant
  useEffect(() => {
    if (chatMode !== "AUDIO") return;
    if (!selectedMicrophoneId) return;

    let cancelled = false;
    let acquiredStream = null;
    let acquiredSource = null;

    const acquire = async () => {
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          deviceId: { exact: selectedMicrophoneId },
        },
        video: false,
      };

      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        if (cancelled) {
          stream.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch (e) {}
          });
          return;
        }

        acquiredStream = stream;
        recordingStreamRef.current = stream;

        const audioContext = recordingAudioContextRef.current;
        const analyser = recordingAnalyserRef.current;
        if (audioContext && analyser) {
          if (audioContext.state === "suspended") {
            await audioContext.resume();
          }
          if (!cancelled) {
            acquiredSource = audioContext.createMediaStreamSource(stream);
            acquiredSource.connect(analyser);
            recordingSourceRef.current = acquiredSource;
          }
        }

        if (!cancelled) setStreamReady(true);
      } catch (err) {
        if (cancelled) return;
        console.error("getUserMedia error:", err.name, err.message);

        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError"
        ) {
          NotificationManager.error(
            "Microphone access was denied. Please grant permission to use your microphone.",
          );
        } else if (
          err.name === "NotFoundError" ||
          err.name === "DevicesNotFoundError"
        ) {
          NotificationManager.error(
            "No microphone detected. Please connect a microphone and try again.",
          );
        } else if (
          err.name === "NotReadableError" ||
          err.name === "TrackStartError"
        ) {
          NotificationManager.error(
            "Your microphone is busy or not accessible. Please close other applications using the microphone.",
          );
        } else if (
          err.name === "OverconstrainedError" ||
          err.name === "ConstraintNotSatisfiedError"
        ) {
          NotificationManager.error(
            "The audio constraints could not be satisfied. Please try again with different settings.",
          );
        } else {
          NotificationManager.error(
            "Failed to access microphone. Please check permissions and try again.",
          );
        }

        setStreamReady(false);
        onErrorRef.current();
      }
    };

    acquire();

    return () => {
      cancelled = true;
      setStreamReady(false);

      // Discard any in-flight recording so onStopHandler doesn't upload it
      shouldProcessAudioRef.current = false;
      if (
        recordingMediaRef.current &&
        recordingMediaRef.current.state !== "inactive"
      ) {
        try {
          recordingMediaRef.current.stop();
        } catch (e) {}
      }

      if (acquiredSource) {
        try {
          acquiredSource.disconnect();
        } catch (e) {}
      }
      recordingSourceRef.current = null;

      if (acquiredStream) {
        acquiredStream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (e) {}
        });
      }
      recordingStreamRef.current = null;
    };
  }, [
    chatMode,
    selectedMicrophoneId,
    recordingAudioContextRef,
    recordingAnalyserRef,
  ]);

  const startRecording = useCallback(async () => {
    if (!recordingCanProceed) return;
    const stream = recordingStreamRef.current;
    if (!stream) return;
    const audioTracks = stream.getAudioTracks();
    if (
      !audioTracks ||
      audioTracks.length === 0 ||
      audioTracks.some((t) => t.readyState !== "live")
    ) {
      return;
    }

    setRecordingCanProceed(false);
    if (recordingIsActiveRef.current || recordingIsStoppedRef.current) {
      stopRecording();
    }

    recordingIsStoppedRef.current = false;

    try {
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000,
      });

      recordingMediaRef.current = mediaRecorder;
      recordingAudioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordingAudioChunksRef.current.push(event.data);
        }
      };

      const onStopHandler = async () => {
        if (!shouldProcessAudioRef.current) return;
        shouldProcessAudioRef.current = false;
        if (recordingAudioChunksRef.current.length === 0) {
          console.warn("[recording] No audio data collected during recording");
          stopRecording();
          return;
        }

        const audioBlob = new Blob(recordingAudioChunksRef.current, {
          type: mimeType,
        });
        recordingAudioChunksRef.current = [];

        if (audioBlob.size === 0) {
          console.warn("[recording] created audio blob is empty");
          stopRecording();
          return;
        }

        // Check if audio contains speech before uploading
        try {
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          const arrayBuffer = await audioBlob.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          const channelData = audioBuffer.getChannelData(0);

          let sumOfSquares = 0;
          for (let i = 0; i < channelData.length; i++) {
            sumOfSquares += channelData[i] * channelData[i];
          }
          const rms = Math.sqrt(sumOfSquares / channelData.length);
          audioContext.close();

          if (rms < SILENCE_THRESHOLD) {
            NotificationManager.error(i18n.t("no_speech_detected"));
            dispatchAudio({ type: "SET_WAITING_FOR_USER_VOICE" });
            return;
          }
        } catch (e) {
          // If analysis fails, proceed with upload anyway
          console.warn("Audio analysis failed, proceeding with upload:", e);
        }

        const fileExtension = mimeType.split(";")[0].split("/")[1] || "webm";
        const audioFile = new File([audioBlob], `user.${fileExtension}`, {
          type: mimeType,
        });

        const formData = new FormData();
        formData.append("file", audioFile);
        formData.append("fileType", "AUDIO");
        formData.append("curriculumType", curriculumType);
        formData.append(
          "path",
          `/${userInformation.id}/${userProfile.id}/audios`,
        );
        formData.append("userId", userInformation.id);
        formData.append("userProfileId", userProfile.id);

        let activityTemplateChatId = -1;
        let activityTemplateChatItemId = -1;
        let subjectDescriptionChatId = -1;
        let subjectDescriptionChatItemId = -1;

        if (chatSubjectDescriptionId) {
          formData.append("subjectDescriptionId", chatSubjectDescriptionId);
          formData.append("subjectDescriptionChatId", String(subjectDescriptionChatId));
          formData.append(
            "subjectDescriptionChatItemId",
            String(subjectDescriptionChatItemId),
          );
        } else {
          formData.append("activityTemplateChatId", String(activityTemplateChatId));
          formData.append(
            "activityTemplateChatItemId",
            String(activityTemplateChatItemId),
          );
          formData.append("activityId", chatActivityId);
          formData.append("activityTemplateId", chatActivityTemplateId);
        }

        try {
          dispatchAudio({ type: "SET_WAITING_FOR_VOICE_CONVERSION" });

          let uploadData;
          let uploadErr;

          if (chatSubjectDescriptionId) {
            ({ data: uploadData, err: uploadErr } =
              await createOrUpdateStorageFileFromSubjectDescriptionAndSubjectDescriptionChatAndSubjectDescriptionChatItem(
                formData,
              ));
          } else {
            ({ data: uploadData, err: uploadErr } =
              await createOrUpdateStorageFileFromActivityTemplateChatAndActivityTemplateChatItem(
                formData,
              ));
          }

          if (uploadErr) throw uploadErr;

          const audioUrl = `${connection.base.storage.url}/storage-files/user-profile/preview/file${uploadData.path}`;

          let speechToTextData;
          let speechToTextErr;
          if (chatSubjectDescriptionId) {
            ({ data: speechToTextData, err: speechToTextErr } =
              await convertSubjectDescriptionChatSpeechToTextWithAI({
                audioUrl,
                userId: userInformation.id,
                userProfileId: userProfile.id,
                brandId,
                campusId,
                institutionId,
                subjectDescriptionId: chatSubjectDescriptionId,
                subjectDescriptionChatId,
                subjectDescriptionChatItemId,
                voiceType: chatVoiceType,
                language: getLanguageFromCode(chatVoiceLanguage),
                actType: chatActType,
              }));
          } else {
            ({ data: speechToTextData, err: speechToTextErr } =
              await convertActivityTemplateChatSpeechToTextWithAI({
                audioUrl,
                userId: userInformation.id,
                userProfileId: userProfile.id,
                brandId,
                campusId,
                institutionId,
                activityId: chatActivityId,
                activityTemplateId: chatActivityTemplateId,
                activityTemplateType: chatActivityTemplateType,
                activityTemplateChatId,
                activityTemplateChatItemId,
                voiceType: chatVoiceType,
                language: getLanguageFromCode(chatVoiceLanguage),
                actType: chatActType,
              }));
          }
          if (speechToTextErr) throw speechToTextErr;

          if (!speechToTextData.text || !speechToTextData.text.trim()) {
            // No words detected — reset to waiting state
            NotificationManager.error(
              i18n.t("no_speech_detected"),
            );
            dispatchAudio({ type: "SET_WAITING_FOR_USER_VOICE" });
            return;
          }

          dispatchAudio({ type: "SET_WAITING_FOR_ASSISTANT_RESPONSE" });
          onSendMessage(speechToTextData.text);
        } catch (error) {
          console.error("Error uploading audio:", error);
          NotificationManager.error(
            "Failed to upload audio. Please try again.",
          );
          // Switch back to TEXT mode on error
          onError();
        } finally {
          stopRecording();
          mediaRecorder.removeEventListener("stop", onStopHandler);
        }
      };

      mediaRecorder.addEventListener("stop", onStopHandler);

      mediaRecorder.onerror = (event: Event) => {
        // MediaRecorder error events carry .error but the DOM typings expose Event only.
        const mediaErr = (event as Event & { error?: { message?: string } }).error;
        console.error("MediaRecorder error:", mediaErr);
        NotificationManager.error(
          "Recording error occurred: " + (mediaErr?.message ?? ""),
        );
        stopRecording();
      };

      const audioContext = recordingAudioContextRef.current;
      if (audioContext && audioContext.state === "suspended") {
        await audioContext.resume();
      }

      mediaRecorder.start(1000);
      recordingIsActiveRef.current = true;
      shouldProcessAudioRef.current = true;
      setRecordingIsActive(true);

      // Hold-to-talk: silence detection commented out — release = stop
      // const dataArray = recordingDataArrayRef.current;
      //
      // const checkSilence = () => {
      //   const isActive = recordingIsActiveRef.current;
      //   const isStopped = recordingIsStoppedRef.current;
      //   if (!isActive || isStopped) return;
      //   analyser.getFloatTimeDomainData(dataArray);
      //   let sumOfSquares = 0;
      //   for (let i = 0; i < dataArray.length; i++) {
      //     sumOfSquares += dataArray[i] * dataArray[i];
      //   }
      //   const rms = Math.sqrt(sumOfSquares / dataArray.length);
      //   if (rms > SOUND_THRESHOLD) recordingCheckSilenceUserSpokenRef.current = true;
      //   if (!recordingCheckSilenceUserSpokenRef.current) { requestAnimationFrame(checkSilence); return; }
      //   if (rms < SILENCE_THRESHOLD) {
      //     if (!recordingCheckSilenceTimeoutRef.current) {
      //       recordingCheckSilenceTimeoutRef.current = setTimeout(() => {
      //         if (recordingIsActiveRef.current && !recordingIsStoppedRef.current) stopRecording();
      //       }, SILENCE_DURATION);
      //     }
      //   } else if (rms > SOUND_THRESHOLD) {
      //     if (recordingCheckSilenceTimeoutRef.current) {
      //       clearTimeout(recordingCheckSilenceTimeoutRef.current);
      //       recordingCheckSilenceTimeoutRef.current = null;
      //     }
      //   }
      //   if (recordingIsActiveRef.current && !recordingIsStoppedRef.current) requestAnimationFrame(checkSilence);
      // };
      // requestAnimationFrame(checkSilence);

      // Safety net: auto-stop after MAX_RECORD_DURATION
      recordingCheckSilenceMaxTimeoutRef.current = setTimeout(() => {
        stopRecording();
      }, MAX_RECORD_DURATION);
    } catch (error) {
      console.error(
        "[recording] startRecording: Failed to start recording:",
        error,
      );
      setRecordingIsActive(false);
      setRecordingCanProceed(true);
      dispatchAudio({ type: "RESET" });
      NotificationManager.error(
        "Failed to access microphone. Please check permissions and try again.",
      );
    }
  }, [
    recordingCanProceed,
    setRecordingCanProceed,
    stopRecording,
    onSendMessage,
    onError,
    dispatchAudio,
    recordingAudioContextRef,
    userInformation,
    userProfile,
    brandId,
    campusId,
    institutionId,
    chatActivityId,
    chatActivityTemplateId,
    chatActivityTemplateType,
    chatSubjectDescriptionId,
    chatVoiceType,
    chatVoiceLanguage,
    chatActType,
  ]);

  const checkMicrophoneCompatibility = () => {
    const hasGetUserMedia = !!(
      navigator.mediaDevices && navigator.mediaDevices.getUserMedia
    );

    const hasMediaRecorder = typeof MediaRecorder !== "undefined";

    const hasAudioContext = !!(
      window.AudioContext || (window as any).webkitAudioContext
    );

    if (!hasGetUserMedia || !hasMediaRecorder || !hasAudioContext) {
      console.error("Browser compatibility issues:", {
        hasGetUserMedia,
        hasMediaRecorder,
        hasAudioContext,
      });

      let errorMessage =
        "Your browser doesn't support all required audio features. ";

      if (!hasGetUserMedia) {
        errorMessage += "Media capture is not available. ";
      }

      if (!hasMediaRecorder) {
        errorMessage += "MediaRecorder API is not available. ";
      }

      if (!hasAudioContext) {
        errorMessage += "AudioContext API is not available. ";
      }

      errorMessage +=
        "Please try using a modern browser like Chrome, Firefox, or Edge.";

      NotificationManager.error(errorMessage);
      return false;
    }

    return true;
  };

  // Hold-to-talk: auto-start recording commented out — user triggers via button press
  // useEffect(() => {
  //   if (
  //     chatMode !== "AUDIO" ||
  //     !audioState.isWaitingForUserVoice ||
  //     recordingIsActive ||
  //     audioState.isPlaying ||
  //     audioState.isConverting
  //   ) {
  //     return;
  //   }
  //   if (!checkMicrophoneCompatibility()) {
  //     return;
  //   }
  //   startRecording();
  // }, [
  //   chatMode,
  //   audioState.isWaitingForUserVoice,
  //   startRecording,
  //   recordingIsActive,
  //   audioState.isPlaying,
  //   audioState.isConverting,
  // ]);

  return {
    startRecording,
    stopRecording,
    discardRecording,
    recordingIsActive,
    streamReady,
  };
};
